const EXPECTED_AUTH_ERROR_CODES = new Set([
  "auth/cancelled-popup-request",
  "auth/invalid-credential",
  "auth/invalid-email",
  "auth/missing-password",
  "auth/popup-closed-by-user",
  "auth/user-disabled",
  "auth/user-not-found",
  "auth/user-token-expired",
  "auth/invalid-user-token",
  "auth/id-token-revoked",
  "auth/id-token-expired",
  "auth/wrong-password",
  "auth/weak-password",
  "auth/password-does-not-meet-requirements",
  "auth/invalid-input",
  "account_not_found",
  "identity_conflict",
  "invalid_code",
  "code_expired",
  "code_used",
  "too_many_attempts",
  "rate_limited",
  "resend_cooldown",
]);

const AUTH_ERROR_CODE_MAP = Object.freeze({
  "auth/internal-error": "auth_provider_failure",
  "auth/network-request-failed": "auth_network_unavailable",
  "auth/popup-blocked": "auth_popup_blocked",
  "auth/too-many-requests": "auth_rate_limited",
  "auth/provider-unavailable": "auth_provider_failure",
  "client-request-timeout": "auth_network_unavailable",
});

export const accountAccessFailureCode = (
  error = null,
  fallback = "unknown_access_failure",
) => {
  const sourceCode = String(
    error?.data?.error || error?.data?.code || error?.code || "",
  )
    .trim()
    .toLowerCase();
  if (EXPECTED_AUTH_ERROR_CODES.has(sourceCode)) return "";
  return AUTH_ERROR_CODE_MAP[sourceCode] || fallback;
};

export const createAccountAccessAttemptId = (kind) =>
  `${kind}_${Array.from(
    window.crypto.getRandomValues(new Uint8Array(16)),
    // Alphabetic encoding preserves all 128 random bits through the existing
    // privacy filters, which reject strings containing phone-like digit counts.
    (byte) => "abcdefghijklmnop"[byte >> 4] + "abcdefghijklmnop"[byte & 15],
  ).join("")}`;

const attemptProperties = (accessAttemptId) =>
  /^(login|claims)_[a-p0-9]{32}$/u.test(accessAttemptId || "")
    ? { accessAttemptId }
    : {};

// Preserve the gateway UUID for exact log correlation through the funnel's
// privacy filter, which intentionally rejects strings with phone-like digits.
const requestTraceId = (value) => {
  const requestId = String(value || "").toLowerCase();
  if (!/^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/u.test(requestId)) return "";
  return [...requestId.replace(/-/g, "")]
    .map((hex) => "abcdefghijklmnop"[parseInt(hex, 16)])
    .join("");
};

export const recordAccountAccessFailure = ({
  trackFunnelAction,
  stage = "unknown",
  error = null,
  errorCode = "",
  operation = "",
  reasonCode = "",
  roleReadOutcome = "",
  browserOnline,
  accessAttemptId = "",
  requestLifecycleStatus = "",
} = {}) => {
  if (typeof trackFunnelAction !== "function") return false;
  const normalizedErrorCode =
    String(errorCode || "")
      .trim()
      .toLowerCase() || accountAccessFailureCode(error);
  if (!normalizedErrorCode) return false;
  const attempt = attemptProperties(accessAttemptId);
  const normalizedStage = String(stage || "unknown")
    .trim()
    .toLowerCase();
  const helperRoleEvidence = ["helper_role_evidence_load", "helper_role_evidence_retry"].includes(normalizedStage);
  const diagnostics = {};
  if (helperRoleEvidence && /^members:(?:ok|unavailable|permission-denied|other)\.employees:(?:ok|unavailable|permission-denied|other)\.helpers:(?:ok|unavailable|permission-denied|other)$/u.test(roleReadOutcome)) {
    diagnostics.roleReadOutcome = roleReadOutcome;
    if (typeof browserOnline === "boolean") diagnostics.browserOnline = browserOnline;
  }
  const transportReason = {
    "client-request-timeout": "client_request_timeout",
    "auth/network-request-failed": "auth_network_request_failed",
  }[String(error?.code || "").toLowerCase()] || "";
  for (const [key, value] of Object.entries({ operation, reasonCode: reasonCode || transportReason })) {
    if (/^[a-z][a-zA-Z0-9_]{0,63}$/u.test(value)) diagnostics[key] = value;
  }
  if (["credential_preparation", "firebase_id_token", "firebase_app_check", "local_auth_headers", "request_dispatch", "response_headers", "response_body", "http_response"].includes(error?.transportPhase)) {
    diagnostics.transportPhase = error.transportPhase;
  }
  if (Number.isFinite(error?.requestLatencyMs) && error.requestLatencyMs >= 0) {
    diagnostics.requestLatencyMs = Math.min(120000, Math.round(error.requestLatencyMs));
  }
  if (Number.isFinite(error?.credentialLatencyMs) && error.credentialLatencyMs >= 0) {
    diagnostics.credentialLatencyMs = Math.min(120000, Math.round(error.credentialLatencyMs));
  }
  if (Number.isInteger(error?.attemptCount) && error.attemptCount >= 1 && error.attemptCount <= 5) {
    diagnostics.attemptCount = error.attemptCount;
  }
  const traceId = requestTraceId(error?.requestId);
  if (traceId) diagnostics.requestTraceId = traceId;
  if (["initial_send", "resend_with_prior_code", "resend_without_prior_code"].includes(requestLifecycleStatus)) {
    diagnostics.requestLifecycleStatus = requestLifecycleStatus;
  }
  try {
    void Promise.resolve(
      trackFunnelAction(
        "account_access_failed",
        {
          action: normalizedStage,
          errorCode: normalizedErrorCode,
          ...diagnostics,
          ...attempt,
        },
        {
          immediate: true,
          once: !helperRoleEvidence && normalizedStage !== "send_login_code",
          key: `${normalizedStage}:${normalizedErrorCode}${attempt.accessAttemptId ? `:${attempt.accessAttemptId}` : ""}`,
        },
      ),
    ).catch(() => {});
  } catch (_error) {}
  return true;
};

export const recordAccountAccessRecoveryCompleted = ({
  trackFunnelAction,
  stage = "unknown",
  accessAttemptId = "",
} = {}) => {
  if (typeof trackFunnelAction !== "function") return false;
  const attempt = attemptProperties(accessAttemptId);
  const normalizedStage = String(stage || "unknown")
    .trim()
    .toLowerCase();
  const helperRoleEvidence = normalizedStage === "helper_role_evidence_load";
  try {
    void Promise.resolve(
      trackFunnelAction(
        "account_access_recovery_completed",
        { action: normalizedStage, outcome: "success", ...attempt },
        {
          immediate: true,
          once: !helperRoleEvidence,
          key: `${normalizedStage}${attempt.accessAttemptId ? `:${attempt.accessAttemptId}` : ""}`,
        },
      ),
    ).catch(() => {});
  } catch (_error) {}
  return true;
};
